<template>
  <v-card class="ma-6">
    <v-card-title>
      <v-icon color="primary" class="mr-6">mdi-truck-delivery-outline</v-icon>
      Order Tracking
      <div class="flex-grow-1"></div>
      <v-switch v-model="showBad" label="Show flagged (bad) orders" class="mr-6" hide-details dense></v-switch>
      <v-text-field color="green" v-model="search" append-icon="search" label="Search by any field" single-line
        hide-details></v-text-field>
    </v-card-title>

    <v-card-text>
      <!-- Authorization warnings -->
      <v-alert v-if="!loadingAuth && !auth.eBayAuthorized" type="warning" dense text border="left" class="mb-3">
        <div class="d-flex align-center">
          <span class="flex-grow-1">The application is not authorized to read eBay orders. Statuses cannot be checked until you authorize.</span>
          <v-btn small color="warning" @click="authorizeEBay">Authorize eBay</v-btn>
        </div>
      </v-alert>

      <v-alert v-if="!loadingAuth && !auth.acumaticaAuthorized" type="warning" dense text border="left" class="mb-3">
        <div class="d-flex align-center">
          <span class="flex-grow-1">The application is not authorized to write tracking numbers to Acumatica. Tracking will not be pushed until you authorize.</span>
          <v-btn small color="warning" @click="authorizeAcumatica">Authorize Acumatica</v-btn>
        </div>
      </v-alert>

      <div class="d-flex align-center" v-if="!loadingAuth">
        <v-chip small :color="auth.eBayAuthorized ? 'green' : 'grey'" text-color="white" class="mr-2">
          <v-icon left small>{{ auth.eBayAuthorized ? 'mdi-check' : 'mdi-alert' }}</v-icon>
          eBay {{ auth.eBayAuthorized ? 'authorized' : 'not authorized' }}
        </v-chip>
        <v-chip small :color="auth.acumaticaAuthorized ? 'green' : 'grey'" text-color="white" class="mr-2">
          <v-icon left small>{{ auth.acumaticaAuthorized ? 'mdi-check' : 'mdi-alert' }}</v-icon>
          Acumatica {{ auth.acumaticaAuthorized ? 'authorized' : 'not authorized' }}
        </v-chip>
        <div class="flex-grow-1"></div>
        <v-btn small text color="primary" @click="reauthorize">Re-authorize&hellip;</v-btn>
      </div>
    </v-card-text>

    <v-data-table dense :headers="headers" :items="filteredItems" :search="search" :loading="loading"
      loading-text="Loading...please wait" :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100, -1] }"
      :items-per-page="50">

      <template v-slot:item.orderDate="{ item }">
        {{ formatDate(item.orderDate) }}
      </template>

      <template v-slot:item.curyOrderTotal="{ item }">
        {{ item.curyOrderTotal | toCurrency }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">{{ item.statusDescription }}</v-chip>
      </template>

      <template v-slot:item.trackingNumber="{ item }">
        <a v-if="item.hasTracking && item.trackingUrl" :href="item.trackingUrl" target="_blank" rel="noopener">
          {{ item.trackingNumber }}
        </a>
        <span v-else-if="item.hasTracking">{{ item.trackingNumber }}</span>
        <span v-else class="grey--text">&mdash;</span>
      </template>

      <template v-slot:item.maxEstimatedDeliveryDate="{ item }">
        <span v-if="item.maxEstimatedDeliveryDate">
          <span v-if="item.minEstimatedDeliveryDate && item.minEstimatedDeliveryDate !== item.maxEstimatedDeliveryDate">
            {{ formatDate(item.minEstimatedDeliveryDate) }} - {{ formatDate(item.maxEstimatedDeliveryDate) }}
          </span>
          <span v-else>
            {{ formatDate(item.maxEstimatedDeliveryDate) }}
          </span>
        </span>
        <span v-else class="grey--text">&mdash;</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn small icon @click="showDetails(item)" title="View details">
          <v-icon small>mdi-information-outline</v-icon>
        </v-btn>
        <v-btn v-if="item.status !== 'Bad'" small icon @click="markBad(item)" title="Flag as bad (stop checking)">
          <v-icon small>mdi-cancel</v-icon>
        </v-btn>
        <v-btn v-else small icon @click="reset(item)" title="Reset status">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Details modal -->
    <v-dialog v-model="dialog" max-width="720px">
      <v-card v-if="selectedItem">
        <v-card-title>
          <v-icon color="primary" class="mr-3">mdi-truck-delivery-outline</v-icon>
          Order {{ selectedItem.orderNbr }}
          <div class="flex-grow-1"></div>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle>{{ selectedItem.orderDesc }}</v-card-subtitle>
        <v-card-text>
          <v-simple-table dense>
            <tbody>
              <tr><td class="font-weight-medium">Status</td><td>{{ selectedItem.statusDescription }}</td></tr>
              <tr><td class="font-weight-medium">Order type</td><td>{{ selectedItem.orderTypeDescription || '—' }}</td></tr>
              <tr><td class="font-weight-medium">eBay status</td><td>{{ selectedItem.eBayOrderStatus || '—' }}</td></tr>
              <tr><td class="font-weight-medium">eBay order #</td><td>{{ selectedItem.eBayPurchaseOrderId || '—' }}</td></tr>
              <tr><td class="font-weight-medium">Vendor ref</td><td>{{ selectedItem.vendorRefNbr || '—' }}</td></tr>
              <tr><td class="font-weight-medium">Tracking #</td>
                <td>
                  <a v-if="selectedItem.trackingUrl" :href="selectedItem.trackingUrl" target="_blank" rel="noopener">{{ selectedItem.trackingNumber }}</a>
                  <span v-else>{{ selectedItem.trackingNumber || '—' }}</span>
                </td>
              </tr>
              <tr><td class="font-weight-medium">Carrier</td><td>{{ selectedItem.shippingCarrierCode || '—' }}</td></tr>
              <tr><td class="font-weight-medium">Service</td><td>{{ selectedItem.shippingServiceCode || '—' }}</td></tr>
              <tr><td class="font-weight-medium">Shipped</td><td>{{ formatDate(selectedItem.shippedDate) }}</td></tr>
              <tr><td class="font-weight-medium">Est. delivery</td>
                <td>{{ formatDate(selectedItem.minEstimatedDeliveryDate) }} &ndash; {{ formatDate(selectedItem.maxEstimatedDeliveryDate) }}</td>
              </tr>
              <tr><td class="font-weight-medium">Pushed to Acumatica</td><td>{{ selectedItem.pushedToAcumatica ? 'Yes' : 'No' }}</td></tr>
              <tr><td class="font-weight-medium">Last checked</td><td>{{ formatDateTime(selectedItem.lastCheckedDateTime) }}</td></tr>
              <tr v-if="selectedItem.lastError"><td class="font-weight-medium red--text">Last error</td><td class="red--text">{{ selectedItem.lastError }}</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { OrderTrackingService } from '../services/api/orderTracking';
import moment from "moment";

const api = new OrderTrackingService();

export default {
  data() {
    return {
      items: [],
      loading: true,
      loadingAuth: true,
      search: '',
      showBad: false,
      dialog: false,
      selectedItem: null,
      auth: { eBayAuthorized: false, acumaticaAuthorized: false },
      headers: [
        { text: 'Order #', value: 'orderNbr' },
        { text: 'Order Type', value: 'orderTypeDescription' },
        { text: 'Description', value: 'orderDesc' },
        { text: 'Order date', value: 'orderDate' },
        { text: 'Total', value: 'curyOrderTotal', align: 'end' },
        { text: 'Status', value: 'status' },
        { text: 'eBay status', value: 'eBayOrderStatus' },
        { text: 'Tracking #', value: 'trackingNumber' },
        { text: 'Est. delivery', value: 'maxEstimatedDeliveryDate' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },

  computed: {
    filteredItems() {
      return this.showBad ? this.items : this.items.filter(item => item.status !== 'Bad');
    },
  },

  created() {
    this.handleAuthCallbackQuery();
    this.loadAuthStatus();
    this.loadOrders();
  },

  methods: {
    formatDate(value) {
      return value ? moment(value).format('MM/DD/YY') : '—';
    },

    formatDateTime(value) {
      return value ? moment(value).format('MM/DD/YY h:mm A') : '—';
    },

    statusColor(status) {
      switch (status) {
        case 'Completed': return 'green';
        case 'TrackingReceived': return 'teal';
        case 'AwaitingTracking': return 'blue-grey';
        case 'Pending': return 'grey';
        case 'NotFoundOnEBay': return 'orange';
        case 'Error': return 'red';
        case 'Bad': return 'red darken-4';
        default: return 'grey';
      }
    },

    loadOrders() {
      this.loading = true;
      api.getOrders()
        .then(data => { this.items = data; })
        .catch(err => this.$notify({ type: 'error', text: 'Could not load orders: ' + err }))
        .finally(() => { this.loading = false; });
    },

    loadAuthStatus() {
      this.loadingAuth = true;
      api.getAuthStatus()
        .then(data => { this.auth = data; })
        .catch(err => this.$notify({ type: 'error', text: 'Could not load authorization status: ' + err }))
        .finally(() => { this.loadingAuth = false; });
    },

    authorizeEBay() {
      api.getEBayAuthorizeUrl()
        .then(data => { window.location.href = data.url; })
        .catch(err => this.$notify({ type: 'error', text: 'Could not start eBay authorization: ' + err }));
    },

    authorizeAcumatica() {
      api.getAcumaticaAuthorizeUrl()
        .then(data => { window.location.href = data.url; })
        .catch(err => this.$notify({ type: 'error', text: 'Could not start Acumatica authorization: ' + err }));
    },

    reauthorize() {
      this.authorizeEBay();
    },

    showDetails(item) {
      this.selectedItem = item;
      this.dialog = true;
    },

    markBad(item) {
      api.markBad(item.orderNbr)
        .then(() => { this.$notify({ type: 'success', text: `Order ${item.orderNbr} flagged as bad.` }); this.loadOrders(); })
        .catch(err => this.$notify({ type: 'error', text: 'Could not flag order: ' + err }));
    },

    reset(item) {
      api.reset(item.orderNbr)
        .then(() => { this.$notify({ type: 'success', text: `Order ${item.orderNbr} reset.` }); this.loadOrders(); })
        .catch(err => this.$notify({ type: 'error', text: 'Could not reset order: ' + err }));
    },

    handleAuthCallbackQuery() {
      const q = this.$route.query;
      if (q.ebayAuth === 'success') this.$notify({ type: 'success', text: 'eBay authorization succeeded.' });
      else if (q.ebayAuth === 'error') this.$notify({ type: 'error', text: 'eBay authorization failed.' });

      if (q.acumaticaAuth === 'success') this.$notify({ type: 'success', text: 'Acumatica authorization succeeded.' });
      else if (q.acumaticaAuth === 'error') this.$notify({ type: 'error', text: 'Acumatica authorization failed.' });
    },
  },
};
</script>
